import React, { useMemo, useEffect, useState, useCallback } from "react";
import { MDBContainer } from "mdb-react-ui-kit";
import { usePageState } from "../context/AppStateContext";
import { useAuth } from "../context/AuthContext";
import { normalizeRole } from "../utils/rolePermissions";
import { getCurrencySymbol } from "../utils/currency";
import "./ListingAgentPage.css";

// ─────────────────────────────────────────────────────────────────────────
// Config — Listing Agent runs on the ThriftERP VM (separate from app API)
// ─────────────────────────────────────────────────────────────────────────
const PHP_API_BASE = "https://erp.thriftops.com/agent-php-api/index.php";
const NODE_API_BASE = "https://erp.thriftops.com/listing-agent-api/api/v1";

const REFRESH_INTERVAL_MS = 30000;

const LISTING_AGENT_ROLES = ["owner", "admin", "seller manager", "editor"];

const STATUS_TABS = [
  { key: "pending", label: "Pending Review" },
  { key: "approved", label: "Approved" },
  { key: "applied", label: "Applied" },
  { key: "rejected", label: "Rejected" },
];

const TYPE_LABELS = {
  price: "Price",
  title: "Title",
  description: "Description",
  category: "Category",
};

const ACTION_VERBS = {
  created: "created",
  updated: "updated",
  approved: "approved",
  rejected: "rejected",
  applied: "applied",
};

// ─────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────
const timeAgo = (dateStr) => {
  if (!dateStr) return "";
  const normalized = dateStr.includes("T")
    ? dateStr
    : dateStr.replace(" ", "T") + "Z";
  const diff = (Date.now() - new Date(normalized).getTime()) / 1000;
  if (diff < 60) return `${Math.floor(diff)}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
};

const truncate = (str, len) => {
  if (!str) return "";
  return str.length > len ? str.slice(0, len) + "…" : str;
};

const fmtMoney = (val, currency) => {
  if (val === null || val === undefined) return "—";
  return `${getCurrencySymbol(currency)}${Number(val).toFixed(2)}`;
};

// ─────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────
const ListingAgentPage = () => {
  const { user } = useAuth();
  const userRoleNormalized = normalizeRole(user?.role);
  const hasAccess = LISTING_AGENT_ROLES.includes(userRoleNormalized);

  const { state, setState } = usePageState("listingAgent");
  const { activeTab = "pending" } = state;

  const [stats, setStats] = useState(null);
  const [recommendations, setRecommendations] = useState([]);
  const [changes, setChanges] = useState([]);
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actioningId, setActioningId] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  // ---- Fetchers (direct fetch, cross-origin to ThriftERP VM) ----
  const fetchStats = useCallback(async () => {
    const res = await fetch(`${PHP_API_BASE}?action=stats`);
    if (!res.ok) throw new Error("Failed to load stats");
    return res.json();
  }, []);

  const fetchRecommendations = useCallback(async (status) => {
    const res = await fetch(
      `${PHP_API_BASE}?action=recommendations&status=${status}&limit=50`,
    );
    if (!res.ok) throw new Error("Failed to load recommendations");
    return res.json();
  }, []);

  const fetchChanges = useCallback(async () => {
    const res = await fetch(`${PHP_API_BASE}?action=monitoring_status`);
    if (!res.ok) throw new Error("Failed to load monitoring status");
    return res.json();
  }, []);

  const fetchActivity = useCallback(async () => {
    const res = await fetch(`${PHP_API_BASE}?action=logs&limit=40`);
    if (!res.ok) throw new Error("Failed to load activity log");
    return res.json();
  }, []);

  const loadAll = useCallback(
    async (status) => {
      setLoading(true);
      setError(null);

      try {
        const [statsRes, recsRes, changesRes, activityRes] = await Promise.all([
          fetchStats(),
          fetchRecommendations(status),
          fetchChanges(),
          fetchActivity(),
        ]);

        setStats(statsRes);
        setRecommendations(recsRes.data || []);
        setChanges(changesRes.recent_changes || []);
        setActivity(activityRes.data || []);
        setLastUpdated(Date.now());
      } catch (err) {
        console.error("Listing Agent fetch error:", err);
        setError(err?.message || "Failed to load Listing Agent data.");
      } finally {
        setLoading(false);
      }
    },
    [fetchStats, fetchRecommendations, fetchChanges, fetchActivity],
  );

  useEffect(() => {
    if (!hasAccess) {
      setLoading(false);
      return;
    }
    loadAll(activeTab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasAccess, activeTab]);

  useEffect(() => {
    if (!hasAccess) return;
    const interval = setInterval(() => loadAll(activeTab), REFRESH_INTERVAL_MS);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasAccess, activeTab]);

  const handleTabChange = (key) => {
    setState({ activeTab: key });
  };

  // ---- Approve / Reject actions (Node API) ----
  const handleApprove = async (rec) => {
    setActioningId(rec.id);
    try {
      // rec.ebay_store_id now comes from the PHP API
      if (!rec.ebay_store_id) {
        throw new Error(
          "No eBay store linked to this listing. Check product_ebay_details.",
        );
      }

      const applyRes = await fetch(
        `http://localhost:3001/api/v1/applyAgentRecommendation/${rec.ebay_store_id}/${rec.ebay_item_id}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            recommendation_id: rec.id,
            recommendation_type: rec.recommendation_type,
            value_suggested: rec.value_suggested,
          }),
        },
      );

      const applyData = await applyRes.json();

      if (!applyRes.ok) {
        throw new Error(
          applyData?.error || applyData?.message || "Failed to apply on eBay",
        );
      }

      // Mark approved in listing agent DB
      const approveRes = await fetch(
        `${NODE_API_BASE}/recommendations/${rec.id}/approve`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({}),
        },
      );

      if (!approveRes.ok) throw new Error("Failed to mark approved in DB");

      setRecommendations((prev) => prev.filter((r) => r.id !== rec.id));
    } catch (err) {
      console.error("Approve error:", err);
      setError(`Could not approve: ${err.message}`);
    } finally {
      setActioningId(null);
    }
  };

  const handleReject = async (recId) => {
    setActioningId(recId);
    try {
      const res = await fetch(
        `${NODE_API_BASE}/recommendations/${recId}/reject`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ reason: "Rejected from app.thriftops.com" }),
        },
      );
      if (!res.ok) throw new Error("Reject failed");

      setRecommendations((prev) => prev.filter((r) => r.id !== recId));
    } catch (err) {
      console.error("Reject error:", err);
      setError("Could not reject recommendation. Please try again.");
    } finally {
      setActioningId(null);
    }
  };

  // ---- Derived stats ----
  const pendingCount = useMemo(() => {
    const pending = (stats?.recommendations || []).find(
      (r) => r.status === "pending",
    );
    return pending?.count || 0;
  }, [stats]);

  if (!hasAccess) {
    return (
      <MDBContainer fluid className="listing-agent-container">
        <div className="empty-state">
          <i className="fas fa-lock"></i>
          <p>You don't have permission to view the Listing Agent.</p>
        </div>
      </MDBContainer>
    );
  }

  return (
    <MDBContainer fluid className="listing-agent-container">
      <h2 className="mb-4">Listing Agent</h2>

      {/* ---- Stat cards ---- */}
      <div className="stats-grid">
        <div className="stat-card">
          <span className="stat-label">Active Listings</span>
          <span className="stat-value">{stats?.listings?.active ?? "—"}</span>
        </div>
        <div className="stat-card stat-accent">
          <span className="stat-label">Competitors Tracked</span>
          <span className="stat-value">{stats?.competitors?.total ?? "—"}</span>
        </div>
        <div className="stat-card stat-warn">
          <span className="stat-label">Pending Review</span>
          <span className="stat-value">{pendingCount}</span>
        </div>
        <div className="stat-card stat-green">
          <span className="stat-label">Changes (24h)</span>
          <span className="stat-value">{stats?.changes_24h ?? "—"}</span>
        </div>
      </div>

      {/* ---- Status tabs ---- */}
      <div
        className="status-tabs"
        role="tablist"
        aria-label="Filter recommendations by status"
      >
        {STATUS_TABS.map((tab) => (
          <button
            key={tab.key}
            type="button"
            role="tab"
            aria-selected={activeTab === tab.key}
            className={`status-tab-btn ${activeTab === tab.key ? "active" : ""}`}
            onClick={() => handleTabChange(tab.key)}
          >
            {tab.label}
          </button>
        ))}

        <button
          type="button"
          className="btn btn-outline-primary btn-sm refresh-btn"
          onClick={() => loadAll(activeTab)}
          disabled={loading}
        >
          {loading ? "Refreshing..." : "Refresh"}
        </button>
      </div>

      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
          <button
            type="button"
            className="btn btn-sm btn-outline-danger ms-2"
            onClick={() => loadAll(activeTab)}
          >
            Retry
          </button>
        </div>
      )}

      {lastUpdated && (
        <div className="last-updated">
          Updated {new Date(lastUpdated).toLocaleTimeString()}
        </div>
      )}

      {/* ---- Recommendations list ---- */}
      {loading ? (
        <div className="py-5 text-center">
          <h4>Loading recommendations...</h4>
        </div>
      ) : recommendations.length === 0 ? (
        <div className="empty-state">
          <i className="fas fa-check-circle"></i>
          <p>No {activeTab} recommendations right now.</p>
        </div>
      ) : (
        <div className="rec-list">
          {recommendations.map((rec) => {
            const confidencePct = Math.round((rec.confidence_score || 0) * 100);
            const refs = (rec.competitor_refs || []).slice(0, 3);
            const isPending = rec.status === "pending";
            const isActioning = actioningId === rec.id;

            return (
              <div key={rec.id} className="rec-card">
                <div className="rec-head">
                  <div className="rec-head-main">
                    <h5 className="rec-title">
                      {truncate(rec.listing_title, 90)}
                    </h5>
                    <div className="rec-meta">
                      <span
                        className={`type-pill type-${rec.recommendation_type}`}
                      >
                        {TYPE_LABELS[rec.recommendation_type] ||
                          rec.recommendation_type}
                      </span>
                      <span className="rec-meta-item">{rec.marketplace}</span>
                      <span className="rec-meta-item">
                        {fmtMoney(rec.current_price, rec.price_currency)}
                      </span>
                      <span className="rec-meta-item">
                        {timeAgo(rec.created_at)}
                      </span>
                    </div>
                  </div>

                  <div className="confidence">
                    <div className="confidence-bar">
                      <div
                        className="confidence-fill"
                        style={{ width: `${confidencePct}%` }}
                      />
                    </div>
                    <span>{confidencePct}%</span>
                  </div>
                </div>

                <div className="rec-diff">
                  <div className="rec-diff-col">
                    <span className="diff-label">Current</span>
                    <span className="diff-before">
                      {truncate(rec.value_before, 120)}
                    </span>
                  </div>
                  <div className="rec-diff-arrow">→</div>
                  <div className="rec-diff-col">
                    <span className="diff-label">Suggested</span>
                    <span className="diff-after">
                      {truncate(rec.value_suggested, 120)}
                    </span>
                  </div>
                </div>

                <p className="rec-reasoning">{rec.reasoning}</p>

                {refs.length > 0 && (
                  <div className="rec-refs">
                    Based on {refs.length} competitor
                    {refs.length > 1 ? "s" : ""}:{" "}
                    {refs.map((r, i) => (
                      <span key={r.competitor_id || i}>
                        {r.external_url ? (
                          <a
                            href={r.external_url}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            #{i + 1}
                          </a>
                        ) : (
                          `#${i + 1}`
                        )}
                        {i < refs.length - 1 ? ", " : ""}
                      </span>
                    ))}
                  </div>
                )}

                {isPending ? (
                  <div className="rec-actions">
                    <button
                      type="button"
                      className="btn btn-success btn-sm"
                      onClick={() => handleApprove(rec)}
                      disabled={isActioning}
                    >
                      {isActioning ? "Working..." : "Approve"}
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline-danger btn-sm"
                      onClick={() => handleReject(rec.id)}
                      disabled={isActioning}
                    >
                      Reject
                    </button>
                  </div>
                ) : (
                  <div className="rec-actions">
                    <span className={`rec-status-badge status-${rec.status}`}>
                      {rec.status}
                    </span>
                    {rec.reviewed_at && (
                      <span className="rec-reviewed-at">
                        {timeAgo(rec.reviewed_at)}
                      </span>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ---- Recent changes ---- */}
      <h5 className="section-title">Recent Competitor Changes</h5>
      <div className="table-wrap">
        <table className="listing-agent-table">
          <thead>
            <tr>
              <th>Listing</th>
              <th>Competitor</th>
              <th>Field</th>
              <th>Before → After</th>
              <th>Detected</th>
            </tr>
          </thead>
          <tbody>
            {changes.length === 0 ? (
              <tr>
                <td colSpan={5} className="empty-cell">
                  No recent changes detected
                </td>
              </tr>
            ) : (
              changes.map((c, i) => (
                <tr key={i}>
                  <td className="strong">{truncate(c.listing_title, 50)}</td>
                  <td>
                    {c.external_url ? (
                      <a
                        href={c.external_url}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {truncate(c.competitor_title || "View listing", 40)}
                      </a>
                    ) : (
                      truncate(c.competitor_title, 40)
                    )}
                  </td>
                  <td>
                    <span className="field-tag">{c.field_name}</span>
                  </td>
                  <td className="mono">
                    <span className="diff-before">
                      {truncate(c.value_before, 30)}
                    </span>{" "}
                    →{" "}
                    <span className="diff-after">
                      {truncate(c.value_after, 30)}
                    </span>
                  </td>
                  <td className="mono">{timeAgo(c.detected_at)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* ---- Activity log ---- */}
      <h5 className="section-title">Activity Log</h5>
      <div className="table-wrap">
        <table className="listing-agent-table">
          <thead>
            <tr>
              <th>Time</th>
              <th>Entity</th>
              <th>Action</th>
              <th>Actor</th>
              <th>Detail</th>
            </tr>
          </thead>
          <tbody>
            {activity.length === 0 ? (
              <tr>
                <td colSpan={5} className="empty-cell">
                  No activity yet
                </td>
              </tr>
            ) : (
              activity.map((log) => {
                let detail = "";
                if (log.payload) {
                  if (log.payload.type) {
                    detail = `${log.payload.type} · confidence ${Math.round(
                      (log.payload.confidence || 0) * 100,
                    )}%`;
                  } else if (log.payload.reason) {
                    detail = log.payload.reason;
                  } else if (log.payload.marketplace) {
                    detail = log.payload.marketplace;
                  } else {
                    detail = JSON.stringify(log.payload).slice(0, 60);
                  }
                }

                return (
                  <tr key={log.id}>
                    <td className="mono">{timeAgo(log.created_at)}</td>
                    <td>
                      <span className="field-tag">{log.entity_type}</span>
                    </td>
                    <td className="strong">
                      {ACTION_VERBS[log.action] || log.action}
                    </td>
                    <td className="mono muted">{log.actor}</td>
                    <td className="muted">{truncate(detail, 60)}</td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </MDBContainer>
  );
};

export default ListingAgentPage;
